import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Switch } from '@/components/ui/switch';
import { ArrowLeft, Settings, Shield, BarChart3, Plus, Activity, AlertCircle } from 'lucide-react';
import { ActivityFeed } from '@/components/dashboard/ActivityFeed';
import CreateRuleDialog from '@/components/dashboard/CreateRuleDialog';
import RuleSettingsDialog from '@/components/dashboard/RuleSettingsDialog';
import TwitterFeed from '@/components/dashboard/TwitterFeed';
import AuthGuard from '@/components/common/AuthGuard';
import { useAuthGuard } from '@/hooks/useAuthGuard';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import type { SocialPlatform, FilteringRule, PlatformStats } from '@/types/social';
import { getPlatformConfig, getAllAvailablePlatforms } from '@/types/social';
import { Alert, AlertDescription } from '@/components/ui/alert';

const PlatformConfigDashboard = () => {
  const { platform } = useParams<{ platform: string }>();
  const navigate = useNavigate();
  const { user, isAuthenticated, isLoading } = useAuthGuard();
  
  // State management
  const [loading, setLoading] = useState(true);
  const [rules, setRules] = useState<FilteringRule[]>([]);
  const [stats, setStats] = useState<PlatformStats>({ totalFiltered: 0, activeRules: 0 });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRule, setEditingRule] = useState<FilteringRule | undefined>();
  // Handle URL tab parameter - set initial tab based on URL
  const urlParams = new URLSearchParams(window.location.search);
  const urlTab = urlParams.get('tab');
  const [activeTab, setActiveTab] = useState(urlTab && ['feed', 'rules', 'overview'].includes(urlTab) ? urlTab : 'feed');
  const [error, setError] = useState<string | null>(null);

  // Platform validation
  const validPlatforms = getAllAvailablePlatforms();
  const currentPlatform = platform?.toLowerCase() as SocialPlatform;
  const isValidPlatform = currentPlatform && validPlatforms.includes(currentPlatform);
  const platformConfig = isValidPlatform ? getPlatformConfig(currentPlatform) : null;

  // Auth and platform validation
  useEffect(() => {
    if (isLoading) return;
    
    if (!isAuthenticated) {
      navigate('/auth');
      return;
    }
    
    if (!isValidPlatform) {
      setError(`Invalid platform: ${platform}`);
      return;
    }
    
    setError(null);
    loadRules();
  }, [isLoading, isAuthenticated, isValidPlatform, platform, navigate, user]);

  const loadRules = async () => {
    if (!user || !currentPlatform) return;

    try {
      const { data: rulesData, error: rulesError } = await supabase
        .from('filtering_rules')
        .select('*')
        .eq('user_id', user.id)
        .contains('platforms', [currentPlatform])
        .order('created_at', { ascending: false });

      if (rulesError) throw rulesError;

      const { count: filteredCount } = await supabase
        .from('filtered_content')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', user.id)
        .eq('platform', currentPlatform);

      const activeRulesCount = rulesData?.filter(rule => rule.is_active).length || 0;

      setRules(rulesData || []);
      setStats({
        totalFiltered: filteredCount || 0,
        activeRules: activeRulesCount,
      });
    } catch (error) {
      console.error('Error loading rules:', error);
      toast.error('Failed to load filtering rules');
    } finally {
      setLoading(false);
    }
  };

  const toggleRule = async (ruleId: string, isActive: boolean) => {
    try {
      const { error } = await supabase
        .from('filtering_rules')
        .update({ is_active: isActive })
        .eq('id', ruleId);

      if (error) throw error;
      
      setRules(prev => prev.map(rule => 
        rule.id === ruleId ? { ...rule, is_active: isActive } : rule
      ));
      
      toast.success(`Rule ${isActive ? 'enabled' : 'disabled'}`);
      loadRules(); // Refresh stats
    } catch (error) {
      console.error('Error toggling rule:', error);
      toast.error('Failed to update rule');
    }
  };

  const deleteRule = async (ruleId: string) => {
    try {
      const { error } = await supabase
        .from('filtering_rules')
        .delete()
        .eq('id', ruleId);

      if (error) throw error;
      
      setRules(prev => prev.filter(rule => rule.id !== ruleId));
      toast.success('Rule deleted successfully');
      loadRules(); // Refresh stats
    } catch (error) {
      console.error('Error deleting rule:', error);
      toast.error('Failed to delete rule');
    }
  };

  // Loading state
  if (isLoading || loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary"></div>
      </div>
    );
  }

  // Error state
  if (error || !isValidPlatform || !platformConfig) {
    return (
      <div className="min-h-screen bg-background p-6">
        <div className="max-w-4xl mx-auto">
          <Alert className="mb-6">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              {error || `Platform "${platform}" not found`}
            </AlertDescription>
          </Alert>
          <Button onClick={() => navigate('/dashboard-sidebar/platforms')} variant="outline">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Platforms
          </Button>
        </div>
      </div>
    );
  }

  return (
    <AuthGuard>
      <div className="min-h-screen bg-background font-sans">
        {/* Header */}
        <div className="bg-card border-b">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between py-6">
              <div className="flex items-center space-x-4">
                <Button
                  variant="ghost"
                  onClick={() => navigate('/dashboard-sidebar/platforms')}
                  className="flex items-center space-x-2"
                >
                  <ArrowLeft className="h-4 w-4" />
                  <span>Back</span>
                </Button>
                <div className="flex items-center space-x-3">
                  <div className="text-2xl">{platformConfig.icon}</div>
                  <div>
                    <h1 className="text-2xl font-bold">
                      {platformConfig.name} Dashboard
                    </h1>
                    <p className="text-sm text-muted-foreground">
                      Monitor your feed and manage filtering rules
                    </p>
                  </div>
                </div>
              </div>
              <Badge variant="secondary">
                {platformConfig.name}
              </Badge>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8">
          {/* Live Activity Feed - Always at top */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold">Live Rule Application Feed</h2>
                <p className="text-muted-foreground">
                  Real-time content analysis and filtering activity
                </p>
              </div>
              <Badge variant="outline" className="flex items-center gap-2">
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                Live Monitoring
              </Badge>
            </div>
            <ActivityFeed />
          </div>

          {/* Platform Rules and Analytics */}
          <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
            <TabsList className="grid grid-cols-2 w-fit">
              <TabsTrigger value="rules" className="flex items-center space-x-2">
                <Shield className="h-4 w-4" />
                <span>Filter Rules</span>
              </TabsTrigger>
              <TabsTrigger value="overview" className="flex items-center space-x-2">
                <BarChart3 className="h-4 w-4" />
                <span>Analytics</span>
              </TabsTrigger>
            </TabsList>

            {/* Filter Rules Tab with Platform Organization */}
            <TabsContent value="rules" className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-semibold">Content Filtering Rules</h2>
                  <p className="text-sm text-muted-foreground">
                    Manage filtering rules organized by platform
                  </p>
                </div>
                <Button onClick={() => setDialogOpen(true)}>
                  <Plus className="h-4 w-4 mr-2" />
                  New Rule
                </Button>
              </div>

              {/* Platform-specific rules section */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center space-x-3">
                    <div className="text-xl">{platformConfig.icon}</div>
                    <span>{platformConfig.name} Rules</span>
                    <Badge variant="secondary">{rules.filter(r => r.is_active).length} active</Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">

                  {rules.length === 0 ? (
                    <div className="py-12">
                      <div className="text-center space-y-4">
                        <Shield className="h-16 w-16 mx-auto text-muted-foreground" />
                        <div>
                          <h3 className="text-xl font-semibold">No filtering rules created</h3>
                          <p className="text-muted-foreground max-w-md mx-auto">
                            Create your first filtering rule to automatically block unwanted content from your {platformConfig.name} feed
                          </p>
                        </div>
                        <Button onClick={() => setDialogOpen(true)} size="lg">
                          <Plus className="h-4 w-4 mr-2" />
                          Create Your First Rule
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="grid gap-4">
                      {rules.map((rule) => (
                        <Card key={rule.id} className="border-l-4 border-l-primary/20">
                          <CardHeader className="pb-3">
                            <div className="flex items-center justify-between">
                              <CardTitle className="text-base">{rule.name}</CardTitle>
                              <div className="flex items-center space-x-2">
                                <Switch
                                  checked={rule.is_active}
                                  onCheckedChange={(checked) => toggleRule(rule.id, checked)}
                                />
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => setEditingRule(rule)}
                                >
                                  <Settings className="h-4 w-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => deleteRule(rule.id)}
                                >
                                  <AlertCircle className="h-4 w-4" />
                                </Button>
                              </div>
                            </div>
                          </CardHeader>
                          <CardContent>
                            <div className="space-y-2">
                              {rule.keywords && rule.keywords.length > 0 && (
                                <div>
                                  <span className="text-sm font-medium">Keywords: </span>
                                  <div className="flex flex-wrap gap-1 mt-1">
                                    {rule.keywords.map((keyword, index) => (
                                      <Badge key={index} variant="secondary" className="text-xs">
                                        {keyword}
                                      </Badge>
                                    ))}
                                  </div>
                                </div>
                              )}
                              {rule.blocked_users && rule.blocked_users.length > 0 && (
                                <div>
                                  <span className="text-sm font-medium">Blocked users: </span>
                                  <div className="flex flex-wrap gap-1 mt-1">
                                    {rule.blocked_users.map((user, index) => (
                                      <Badge key={index} variant="outline" className="text-xs">
                                        @{user}
                                      </Badge>
                                    ))}
                                  </div>
                                </div>
                              )}
                              {rule.platforms && (
                                <div>
                                  <span className="text-sm font-medium">Platforms: </span>
                                  <span className="text-sm text-muted-foreground">
                                    {rule.platforms.join(', ')}
                                  </span>
                                </div>
                              )}
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Analytics Tab */}
            <TabsContent value="overview" className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">
                      Total Filtered
                    </CardTitle>
                    <Shield className="h-4 w-4 text-muted-foreground" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{stats.totalFiltered}</div>
                    <p className="text-xs text-muted-foreground">
                      Content items blocked
                    </p>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">
                      Active Rules
                    </CardTitle>
                    <Settings className="h-4 w-4 text-muted-foreground" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{stats.activeRules}</div>
                    <p className="text-xs text-muted-foreground">
                      Filtering rules enabled
                    </p>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">
                      Platform Status
                    </CardTitle>
                    <Activity className="h-4 w-4 text-muted-foreground" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold text-green-600">Active</div>
                    <p className="text-xs text-muted-foreground">
                      Monitoring enabled
                    </p>
                  </CardContent>
                </Card>
              </div>
              
              <Card>
                <CardHeader>
                  <CardTitle>Analytics Overview</CardTitle>
                </CardHeader>
                <CardContent>
                  <Alert>
                    <BarChart3 className="h-4 w-4" />
                    <AlertDescription>
                      Detailed analytics and performance metrics are coming soon. 
                      Monitor your filtering effectiveness and content insights.
                    </AlertDescription>
                  </Alert>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>

        {/* Create Rule Dialog */}
        <CreateRuleDialog
          open={dialogOpen}
          onOpenChange={setDialogOpen}
          onRuleCreated={loadRules}
          platform={currentPlatform}
        />

        {/* Rule Settings Dialog */}
        <RuleSettingsDialog
          open={!!editingRule}
          onOpenChange={(open) => !open && setEditingRule(undefined)}
          rule={editingRule || null}
          onRuleUpdated={loadRules}
          onRuleDeleted={() => {
            loadRules();
            setEditingRule(undefined);
          }}
        />
      </div>
    </AuthGuard>
  );
};

export default PlatformConfigDashboard;